import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSip } from "@/contexts/SipContext";
import { 
  Phone, 
  PhoneOff, 
  Mic, 
  MicOff, 
  Pause, 
  Play,
  Maximize2,
  Minimize2,
  GripHorizontal,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export default function FloatingCallWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const sip = useSip();
  
  const { 
    callStatus, 
    callDuration, 
    isMuted, 
    isOnHold,
    incomingCallInfo,
    callerDetails,
    toggleMute,
    toggleHold,
    hangUp
  } = sip;

  // Widget state
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  // Only show when in a call and NOT on the dialer page
  const isOnDialerPage = location.pathname === '/app/dialer';
  const hasActiveCall = callStatus === 'connected' || callStatus === 'ringing';
  const shouldShow = hasActiveCall && !isOnDialerPage;

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get caller display name
  const getCallerName = () => {
    if (callerDetails?.name) return callerDetails.name;
    if (incomingCallInfo?.name && incomingCallInfo.name !== incomingCallInfo.number) {
      return incomingCallInfo.name;
    }
    return incomingCallInfo?.number || 'Unknown';
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return; // Don't drag when clicking buttons
    setIsDragging(true);
    offsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 300, e.clientX - offsetRef.current.x));
    const newY = Math.max(0, Math.min(window.innerHeight - 150, e.clientY - offsetRef.current.y));
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Reset position when widget first appears
  useEffect(() => {
    if (shouldShow) {
      setPosition({ x: window.innerWidth - 320, y: 20 });
    }
  }, [shouldShow]);

  if (!shouldShow) return null;

  return (
    <div
      ref={dragRef}
      className="fixed z-50 select-none"
      style={{ 
        left: position.x, 
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
      data-testid="floating-call-widget"
    >
      <Card className={`shadow-2xl border-2 ${isOnHold ? 'border-yellow-500' : 'border-green-500'} bg-background/95 backdrop-blur-sm`}>
        {/* Header - Draggable area */}
        <div 
          className="flex items-center justify-between px-3 py-2 border-b cursor-grab active:cursor-grabbing bg-muted/50"
          onMouseDown={handleMouseDown}
        >
          <div className="flex items-center gap-2">
            <GripHorizontal className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Active Call</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? "Expand" : "Minimize"}
            >
              {isMinimized ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => navigate('/app/dialer')}
              title="Go to Dialer"
            >
              <Phone className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="p-3 space-y-3">
            {/* Caller Info */}
            <div className="text-center">
              <p className="font-semibold truncate max-w-[250px]" title={getCallerName()}>
                {getCallerName()}
              </p>
              {callerDetails?.name && incomingCallInfo?.number && (
                <p className="text-xs text-muted-foreground font-mono">
                  {incomingCallInfo.number}
                </p>
              )}
              <div className="flex items-center justify-center gap-2 mt-1">
                <div className={`w-2 h-2 rounded-full ${isOnHold ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`} />
                <span className="text-sm font-mono">
                  {isOnHold ? 'On Hold' : formatDuration(callDuration)}
                </span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-2">
              <Button
                variant={isMuted ? "destructive" : "outline"}
                size="sm"
                onClick={toggleMute}
                className="h-9 px-3"
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              
              <Button
                variant={isOnHold ? "destructive" : "outline"}
                size="sm"
                onClick={toggleHold}
                className="h-9 px-3"
                title={isOnHold ? "Resume" : "Hold"}
              >
                {isOnHold ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={hangUp}
                className="h-9 px-3"
                title="End Call"
              >
                <PhoneOff className="w-4 h-4" />
              </Button>
            </div>

            {/* Quick action */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/app/dialer')}
              className="w-full text-xs"
            >
              <Phone className="w-3 h-3 mr-2" />
              Open Full Dialer
            </Button>
          </div>
        )}

        {/* Minimized view */}
        {isMinimized && (
          <div className="px-3 py-2 flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${isOnHold ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`} />
            <span className="text-sm font-medium truncate max-w-[120px]">{getCallerName()}</span>
            <span className="text-xs font-mono text-muted-foreground">
              {isOnHold ? 'Hold' : formatDuration(callDuration)}
            </span>
            <Button
              variant="destructive"
              size="icon"
              className="h-6 w-6 ml-auto"
              onClick={hangUp}
            >
              <PhoneOff className="w-3 h-3" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
